import { useState, useRef, useEffect } from 'react';
import { Check, Clock, Trash2, GripVertical, Play, Zap, Timer, List, MoreVertical, FolderIcon } from 'lucide-react';
import { Task, Tag, FocusSession, Folder } from '@/types/task';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { usePomodoro } from '@/hooks/usePomodoroTimer';
import { useAuth } from '@/hooks/useAuth';
import { useProductTour } from '@/hooks/useProductTour';
import { useHaptic } from '@/hooks/useHaptic';
import { TimerModeDialog } from '@/components/TimerModeDialog';
import { TimerMode } from '@/types/task';
import { LogTimeDialog } from '@/components/LogTimeDialog';
import { saveFocusSession, updateFocusSession, getFocusSessionsForTask } from '@/hooks/useFocusSessions';
import { useToast } from '@/hooks/use-toast';
import { TagBadge } from '@/components/TagBadge';
import { TagSelector } from '@/components/TagSelector';
import { FolderSelector } from '@/components/FolderSelector';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface TaskItemProps {
  task: Task;
  tags: Tag[];
  folders?: Folder[];
  onToggle: (id: string) => void;
  onDelete: (id: string) => void;
  onUpdateTask?: (id: string, updates: Partial<Task>) => void;
  onMoveToBacklog?: (id: string) => void;
  onCreateTag?: (name: string, color: string) => Promise<Tag | null>;
  onCreateFolder?: (name: string, color: string) => Promise<Folder | null>;
  isDragging?: boolean;
  dragHandleProps?: React.HTMLAttributes<HTMLDivElement>;
  isMainTask?: boolean;
}

export function TaskItem({ task, tags, folders = [], onToggle, onDelete, onUpdateTask, onMoveToBacklog, onCreateTag, onCreateFolder, isDragging, dragHandleProps, isMainTask }: TaskItemProps) {
  const selectedTag = tags.find(t => t.id === task.tag_id);
  const taskFolder = folders.find(f => f.id === task.folder_id);
  
  const handleTagChange = (tagId: string | null) => {
    if (onUpdateTask) {
      onUpdateTask(task.id, { tag_id: tagId });
    }
  };

  const handleFolderChange = (folderId: string | null) => {
    if (onUpdateTask) {
      onUpdateTask(task.id, { folder_id: folderId });
    }
  };
  const [isHovered, setIsHovered] = useState(false);
  const [showWorkTypeDialog, setShowWorkTypeDialog] = useState(false);
  const [showLogTimeDialog, setShowLogTimeDialog] = useState(false);
  const [logTimeExistingSessions, setLogTimeExistingSessions] = useState<FocusSession[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(task.content);
  const inputRef = useRef<HTMLInputElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showFolderSelector, setShowFolderSelector] = useState(false);
  const menuPointerDownRef = useRef<{ x: number; y: number } | null>(null);
  const menuPointerUpRef = useRef<{ x: number; y: number } | null>(null);
  const { startFocus, activeTask, timerState } = usePomodoro();
  const { user } = useAuth();
  const { toast } = useToast();
  const { notifyAction } = useProductTour();
  const { triggerSuccess, triggerHeavy } = useHaptic();
  
  const isCurrentlyFocused = activeTask?.id === task.id && timerState !== 'idle';

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  useEffect(() => {
    if (!showLogTimeDialog || !user) return;
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    getFocusSessionsForTask(user.id, task.id, start, end).then((sessions) => {
      setLogTimeExistingSessions(sessions);
    });
  }, [showLogTimeDialog, user, task.id]);

  const handleSave = () => {
    if (editValue.trim() && editValue !== task.content && onUpdateTask) {
      onUpdateTask(task.id, { content: editValue.trim() });
    } else {
      setEditValue(task.content);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      setEditValue(task.content);
      setIsEditing(false);
    }
  };

  const formatDuration = (minutes: number) => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const handleStartFocus = (mode: TimerMode, duration: number | null) => {
    setShowWorkTypeDialog(false);
    triggerHeavy(); // Haptic feedback for focus mode start
    startFocus(task, mode, duration);
    notifyAction('select-mode');
  };

  const handleLogTime = async (minutes: number) => {
    if (!user) return;

    const { error } = await saveFocusSession({
      userId: user.id,
      taskId: task.id,
      taskContent: task.content,
      workType: 'focus',
      timerMode: 'manager',
      durationMinutes: minutes,
      startedAt: new Date(),
    });

    if (error) {
      toast({
        title: 'Error logging time',
        description: 'Please try again.',
        variant: 'destructive',
      });
    } else {
      toast({
        title: 'Time logged',
        description: `${minutes >= 60 ? `${Math.floor(minutes / 60)}h ${minutes % 60}m` : `${minutes}m`} logged for "${task.content}"`,
      });
      setShowLogTimeDialog(false);
    }
  };

  const handleEditTime = async (sessionId: string, minutes: number) => {
    const { error } = await updateFocusSession(sessionId, { duration_minutes: minutes });
    if (error) {
      toast({
        title: 'Error updating time',
        description: 'Please try again.',
        variant: 'destructive',
      });
    } else {
      toast({
        title: 'Time updated',
        description: `Updated to ${minutes >= 60 ? `${Math.floor(minutes / 60)}h ${minutes % 60}m` : `${minutes}m`}`,
      });
      setShowLogTimeDialog(false);
    }
  };

  return (
    <>
      <div
        className={cn(
          "group flex items-center gap-3 rounded-xl bg-card border border-border/50 task-shadow transition-shadow duration-200",
          isMainTask ? "p-4" : "p-3",
          isDragging && "task-shadow-hover ring-2 ring-primary/20",
          isCurrentlyFocused && "ring-2 ring-primary/50 border-primary/30",
          isMainTask && "border-primary/30 bg-gradient-to-r from-primary/5 to-transparent",
          task.completed && "opacity-60",
          task.is_side_quest && "border-l-[3px] border-l-[hsl(var(--side-quest))]"
        )}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
          {/* Checkbox */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              triggerSuccess(); // Haptic celebration for task complete
              onToggle(task.id);
            }}
            data-tour-target="task-checkbox"
            aria-label={task.completed ? 'Mark task incomplete' : 'Mark task complete'}
            className={cn(
              "flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center touch-manipulation",
              "transition-all duration-200",
              task.completed
                ? "bg-success border-success text-success-foreground animate-check-bounce"
                : "border-muted-foreground/30 hover:border-primary"
            )}
          >
            {task.completed && <Check className="h-3.5 w-3.5" />}
          </button>

          {/* Content */}
          <div className="flex-1 min-w-0">
            {isEditing ? (
              <Input
                ref={inputRef}
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onBlur={handleSave}
                onKeyDown={handleKeyDown}
                className="h-7 text-sm py-0 px-2"
              />
            ) : (
              <p 
                className={cn(
                  "text-sm font-medium break-words transition-all duration-200 cursor-text hover:bg-muted/50 rounded px-1 -mx-1",
                  task.completed && "line-through text-muted-foreground"
                )}
                onClick={() => !task.completed && onUpdateTask && setIsEditing(true)}
              >
                {task.content}
              </p>
            )}
            
            {/* Time info and badges */}
            {!isEditing && (
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                {/* Tag Selector */}
                {!task.completed && onCreateTag && (
                  <TagSelector
                    tags={tags}
                    selectedTagId={task.tag_id}
                    onSelect={handleTagChange}
                    onCreateTag={onCreateTag}
                    compact
                  />
                )}
                {/* Show tag badge for completed tasks */}
                {task.completed && selectedTag && (
                  <TagBadge name={selectedTag.name} color={selectedTag.color} size="sm" />
                )}
                {task.is_side_quest && (
                  <Badge 
                    variant="outline" 
                    className="text-[10px] px-1.5 py-0 h-5 border-[hsl(var(--side-quest))]/50 text-[hsl(var(--side-quest))] bg-[hsl(var(--side-quest))]/10"
                  >
                    <Zap className="h-2.5 w-2.5 mr-0.5" />
                    Side Quest
                  </Badge>
                )}
                {task.due_time && (
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {formatTime(task.due_time)}
                  </span>
                )}
                {task.duration && (
                  <span className="text-xs text-accent-foreground bg-accent px-1.5 py-0.5 rounded">
                    {formatDuration(task.duration)}
                  </span>
                )}
                {taskFolder && (
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: taskFolder.color }} />
                    <span className="text-xs text-muted-foreground">{taskFolder.name}</span>
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Action Buttons - Always visible */}
          <div className="flex items-center gap-1">
            {/* Focus Button */}
            {!task.completed && (
              <TooltipProvider delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      data-tour-target="start-task-button"
                      aria-label="Start focus session"
                      onClick={(e) => {
                        e.stopPropagation();
                        notifyAction('start-task');
                        setShowWorkTypeDialog(true);
                      }}
                      disabled={isCurrentlyFocused}
                      className={cn(
                        "h-10 w-10 touch-manipulation",
                        isCurrentlyFocused 
                          ? "text-primary" 
                          : "text-muted-foreground/50 hover:text-primary"
                      )}
                    >
                      <Play className="h-5 w-5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Start focus session</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {/* Options Menu - Both mobile and desktop (tap-vs-scroll: only open on intentional tap) */}
            <DropdownMenu open={menuOpen} onOpenChange={(open) => {
              if (open) {
                const down = menuPointerDownRef.current;
                const up = menuPointerUpRef.current;
                if (down && up && Math.hypot(up.x - down.x, up.y - down.y) > 10) {
                  setMenuOpen(false);
                  menuPointerDownRef.current = null;
                  menuPointerUpRef.current = null;
                  return;
                }
              }
              setMenuOpen(open);
              menuPointerDownRef.current = null;
              menuPointerUpRef.current = null;
            }}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  data-tour-target="move-to-backlog-trigger"
                  aria-label="Task options"
                  onClick={(e) => e.stopPropagation()}
                  onPointerDown={(e) => { menuPointerDownRef.current = { x: e.clientX, y: e.clientY }; }}
                  onPointerUp={(e) => { menuPointerUpRef.current = { x: e.clientX, y: e.clientY }; }}
                  onPointerCancel={() => { menuPointerDownRef.current = null; menuPointerUpRef.current = null; }}
                  className="h-10 w-10 touch-manipulation text-muted-foreground/50 hover:text-primary"
                >
                  <MoreVertical className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[180px]">
                <DropdownMenuItem 
                  onClick={() => setShowLogTimeDialog(true)}
                  className="py-3 text-base"
                >
                  <Timer className="h-5 w-5 mr-3" />
                  Log Time
                </DropdownMenuItem>
                {onCreateFolder && (
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.preventDefault();
                      setMenuOpen(false);
                      setTimeout(() => setShowFolderSelector(true), 150);
                    }}
                    className="py-3 text-base"
                  >
                    <FolderIcon className="h-5 w-5 mr-3" />
                    {task.folder_id ? 'Change Folder' : 'Move to Folder'}
                  </DropdownMenuItem>
                )}
                {onMoveToBacklog && (
                  <DropdownMenuItem 
                    onClick={() => onMoveToBacklog(task.id)}
                    className="py-3 text-base"
                  >
                    <List className="h-5 w-5 mr-3" />
                    Move to All Todos
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem 
                  onClick={() => setShowDeleteConfirm(true)}
                  className="text-destructive focus:text-destructive py-3 text-base"
                >
                  <Trash2 className="h-5 w-5 mr-3" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Drag Handle - Right side, always visible */}
          {dragHandleProps && (
            <div
              {...dragHandleProps}
              className="cursor-grab active:cursor-grabbing text-muted-foreground/50 touch-manipulation p-1"
            >
              <GripVertical className="h-5 w-5" />
            </div>
          )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete task?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => {
              onDelete(task.id);
              setShowDeleteConfirm(false);
            }}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <TimerModeDialog
        open={showWorkTypeDialog}
        onOpenChange={setShowWorkTypeDialog}
        onSelect={handleStartFocus}
        taskName={task.content}
      />

      <LogTimeDialog
        open={showLogTimeDialog}
        onOpenChange={setShowLogTimeDialog}
        onLog={handleLogTime}
        onEdit={handleEditTime}
        taskName={task.content}
        existingSessions={logTimeExistingSessions}
      />

      {/* Folder selector triggered from dropdown menu */}
      {showFolderSelector && folders.length >= 0 && onCreateFolder && (
        <FolderSelector
          folders={folders}
          selectedFolderId={task.folder_id}
          onSelect={(folderId) => {
            handleFolderChange(folderId);
            setShowFolderSelector(false);
          }}
          onCreateFolder={onCreateFolder}
          triggerOpen={showFolderSelector}
          onOpenChange={(open) => { if (!open) setShowFolderSelector(false); }}
        />
      )}
    </>
  );
}
